import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { v4 as uuidv4 } from 'uuid';

/**
 * Every state transition of a single execute() run. The flow can stall with no code on the
 * stack (see the CRD-2124 comment in challengeHelpers), so a step that never shows up is as
 * meaningful as one that does: the last step reached tells you exactly where it died.
 */
export enum ThreeDSStep {
  SETUP_JWT_REQUEST = 'setup_jwt_request',
  SETUP_JWT_RECEIVED = 'setup_jwt_received',
  SETUP_STARTED = 'setup_started',
  SETUP_COMPLETE = 'setup_complete',
  SETUP_FAILED = 'setup_failed',
  EXECUTE_START = 'execute_start',
  CHECK_ENROLL_REQUEST = 'check_enroll_request',
  CHECK_ENROLL_RESPONSE = 'check_enroll_response',
  FRICTIONLESS_RESOLVED = 'frictionless_resolved',
  CHALLENGE_DATA_INCOMPLETE = 'challenge_data_incomplete',
  CHALLENGE_REQUESTED = 'challenge_requested',
  CHALLENGE_UI_RENDERED = 'challenge_ui_rendered',
  CHALLENGE_UI_CLOSED = 'challenge_ui_closed',
  CARDINAL_VALIDATED = 'cardinal_validated',
  VALIDATE_REQUEST = 'validate_request',
  VALIDATE_RESPONSE = 'validate_response',
  ORDER_DATA_EMITTED = 'order_data_emitted',
  AUTHENTICATION_DECLINED = 'authentication_declined',
  ERROR_EMITTED = 'error_emitted',
  EXECUTE_RESOLVED = 'execute_resolved',
  EXECUTE_REJECTED = 'execute_rejected',
  LISTENERS_DISPOSED = 'listeners_disposed',
  PAGE_BACKGROUNDED = 'page_backgrounded',
  STILL_PENDING = 'still_pending',
  ABANDONED = 'abandoned',
}

/**
 * A stalled flow produces no events at all, so the watchdog is what turns silence into a
 * trace. These are observation points only, spaced to cover a plausible challenge (an SMS OTP
 * takes a minute or two) without flooding; they all fall before THREE_DS_FLOW_TIMEOUT_MS,
 * which is what actually ends the flow and writes the terminal record.
 */
const WATCHDOG_MARKS_MS = [30_000, 120_000, 300_000];

const LOG_MESSAGE_PREFIX = '3DS flow';

/**
 * Every record used to read just "3DS flow" in the log list, so telling one row from another
 * meant opening it.
 *
 * Ordered by what has to survive a narrow Message column, which clips from the right: the
 * charge first, then the checkout, then the step. Nothing is dropped for size -- the SDK caps
 * a serialised event at 256KB and discards it above that, while these run around 1KB -- so
 * the order is only about what stays readable at a glance. Segments the flow does not have
 * yet are skipped rather than left as gaps: chargeId is null during setup, since the charge
 * is created after the checkout.
 */
const buildMessage = (step: ThreeDSStep, context: ThreeDSTraceContext) =>
  [LOG_MESSAGE_PREFIX, context.chargeId, context.checkoutId, step, context.entityName].filter(Boolean).join(' · ');

/**
 * Kept free of identifiers on purpose. Error Tracking groups by the Error's own message, so
 * putting a charge id in here would file every failure as its own separate issue.
 */
const errorMessageFor = (step: ThreeDSStep) => `${LOG_MESSAGE_PREFIX}: ${step}`;

/**
 * Every attribute goes under this one key. startLogsAssembly merges the message context LAST,
 * on top of `service`, `session_id`, `view`, `usr`, `status`, `message`, `date` and `error`,
 * so a flat attribute sharing one of those names silently overwrites the real one: `source`
 * would break intake routing and `error` would break Error Tracking grouping. Namespacing
 * removes the whole class of collision and gives a tidy `@three_ds.*` facet tree.
 */
const NAMESPACE = 'three_ds';

/**
 * How a flow ended, so the two reasons a payment does not go through stay separable. A
 * cardholder failing a challenge is an expected business result and should not sit in the
 * same bucket -- or the same Error Tracking issue list -- as something that actually broke.
 */
export enum ThreeDSOutcome {
  DECLINED = 'declined',
  FAILED = 'failed',
}

const RUM_ACTION_PREFIX = '3ds';

type TraceLevel = 'info' | 'warn' | 'error';

export interface ThreeDSTraceContext {
  checkoutId?: string | null;
  chargeId?: string | null;
  entityName?: string | null;
  companyId?: string | null;
  affiliation?: string | null;
  acquirer?: string | null;
  liveMode?: boolean;
  isMobile?: boolean;
  authTransactionId?: string | null;
  requireChallenge?: boolean;
  outcome?: ThreeDSOutcome | null;
}

export interface ThreeDSTracer {
  traceId: string;
  step: (step: ThreeDSStep, data?: Record<string, any>) => void;
  /** Something broke. Reaches Error Tracking. */
  fail: (step: ThreeDSStep, data?: Record<string, any>, cause?: unknown) => void;
  /** The cardholder or issuer refused. Expected, so it warns and carries no Error. */
  decline: (step: ThreeDSStep, data?: Record<string, any>, cause?: unknown) => void;
  settle: (step: ThreeDSStep, data?: Record<string, any>, cause?: unknown) => void;
  enrich: (context: ThreeDSTraceContext) => void;
  getContext: () => ThreeDSTraceContext;
  dispose: () => void;
}

const attributesFor = (context: ThreeDSTraceContext, traceId?: string) => ({
  trace_id: traceId ?? null,
  checkout_id: context.checkoutId ?? null,
  charge_id: context.chargeId ?? null,
  entity: context.entityName ?? null,
  company_id: context.companyId ?? null,
  affiliation: context.affiliation ?? null,
  acquirer: context.acquirer ?? null,
  live_mode: context.liveMode ?? null,
  is_mobile: context.isMobile ?? null,
  auth_transaction_id: context.authTransactionId ?? null,
  require_challenge: context.requireChallenge ?? null,
  outcome: context.outcome ?? null,
});

/**
 * What this flow throws is an Error, an axios payload, or the bare `{ source, data }` object
 * the hook rejects with. The SDK fills error.message / error.kind / error.stack from a real
 * Error only, so normalize before handing it over.
 */
const toError = (cause: unknown, fallbackMessage: string): Error => {
  if (cause instanceof Error) return cause;
  const message = typeof cause === 'string' ? cause : ((cause as any)?.message ?? fallbackMessage);
  return new Error(message);
};

/**
 * Detail of a non-Error rejection, kept inside the namespace so `error.*` stays the SDK's.
 *
 * Cardinal's fields are mapped explicitly because a failed challenge arrives as
 * {ActionCode, ErrorNumber, ErrorDescription} and nothing else -- reading only message/code
 * dropped the entire reason on the one trace that exists to explain it.
 *
 * Deliberately an allow list rather than a pass-through of whatever was thrown: a checkEnroll
 * response also reaches here (see checkIfRequiresChallenge), and that object carries the CAVV
 * cryptogram and the paReq challenge payload.
 */
const describeCause = (cause: unknown): Record<string, any> | undefined => {
  if (!cause) return undefined;
  if (typeof cause === 'string') return { message: cause };
  const value = cause as any;
  // emitError rejects with a { source, data } envelope, so by the time execute() catches it
  // the detail sits one level in. Reading the envelope directly is why the terminal record
  // carried nothing but nulls, and Datadog hides an attribute whose every field is null.
  const detail = value.source !== undefined && value.data ? value.data : value;
  if (typeof detail === 'string') return { message: detail };
  const described = {
    message: detail.message ?? detail.ErrorDescription ?? null,
    code: detail.code ?? detail.status ?? detail.ErrorNumber ?? null,
    type: detail.type ?? null,
    action_code: detail.ActionCode ?? null,
  };
  // Nothing worth a nested object: let the top-level error_source carry the story instead.
  return Object.values(described).some((field) => field !== null) ? described : undefined;
};

const causeAttributes = (cause?: unknown) => {
  const described = describeCause(cause);
  return described ? { cause: described } : {};
};

/**
 * Cardinal setup runs once per page load, long before execute(), and owns its own way to
 * fail: the JWT call can reject, and Cardinal can accept setup('init') and never fire
 * SETUP_COMPLETE, in which case the submit button never arms and the payment dies with the
 * backend having seen nothing at all. Same namespace as the flow steps so one query covers
 * a whole payment.
 */
export const traceSetupStep = (step: ThreeDSStep, context: ThreeDSTraceContext = {}, cause?: unknown) => {
  const attributes = { ...attributesFor(context), step, ...causeAttributes(cause) };
  try {
    const payload = { [NAMESPACE]: attributes };
    const message = buildMessage(step, context);
    if (cause) {
      datadogLogs.logger.error(message, payload, toError(cause, errorMessageFor(step)));
    } else {
      datadogLogs.logger.info(message, payload);
    }
    // The action name stays low cardinality: RUM aggregates by it, so an id in here would
    // make every payment its own action.
    datadogRum.addAction(`${RUM_ACTION_PREFIX}.${step}`, payload);
  } catch (e) {
    console.error('3DS trace: could not emit setup step', e);
  }
};

export const create3DSTracer = (initialContext: ThreeDSTraceContext = {}): ThreeDSTracer => {
  const traceId = uuidv4();
  const startedAt = Date.now();
  const sequence: ThreeDSStep[] = [];
  const timings: Record<string, number> = {};
  const timers: ReturnType<typeof setTimeout>[] = [];

  let context: ThreeDSTraceContext = { ...initialContext };
  let lastStep: ThreeDSStep = ThreeDSStep.EXECUTE_START;
  let settled = false;
  let disposed = false;
  let abandoned = false;

  const elapsed = () => Date.now() - startedAt;

  const baseAttributes = () => attributesFor(context, traceId);

  // First outcome wins, like the promise it mirrors: a decline is what ended the flow even
  // though the rejection that follows travels the same path as a real failure.
  const markOutcome = (outcome: ThreeDSOutcome) => {
    if (!context.outcome) context = { ...context, outcome };
  };

  const terminalLevel = (step: ThreeDSStep): TraceLevel => {
    if (step === ThreeDSStep.EXECUTE_RESOLVED) return 'info';
    return context.outcome === ThreeDSOutcome.DECLINED ? 'warn' : 'error';
  };

  const log = (level: TraceLevel, attributes: Record<string, any>, cause?: unknown) => {
    try {
      const payload = { [NAMESPACE]: attributes };
      const message = buildMessage(attributes.step, context);
      if (level === 'error') {
        datadogLogs.logger.error(message, payload, toError(cause, errorMessageFor(attributes.step)));
        return;
      }
      if (level === 'warn') {
        // No Error attached on purpose: that is what would file an expected decline as an
        // Error Tracking issue.
        datadogLogs.logger.warn(message, payload);
        return;
      }
      datadogLogs.logger.info(message, payload);
    } catch (e) {
      // console.error, not warn: forwardConsoleLogs only carries 'error' and 'info', so a
      // warn here would be invisible in the very place we are trying to fix.
      console.error('3DS trace: could not emit log', e);
    }
  };

  /**
   * RUM is where this reads as a timeline: addAction puts each step on the session's event
   * list with its attributes, addTiming marks the view waterfall. A stalled flow then shows
   * as a challenge_requested with nothing after it. Both buffer calls made before init.
   */
  const rum = (step: ThreeDSStep, attributes: Record<string, any>) => {
    try {
      datadogRum.addAction(`${RUM_ACTION_PREFIX}.${step}`, { [NAMESPACE]: attributes });
      datadogRum.addTiming(`${RUM_ACTION_PREFIX}_${step}`);
    } catch (e) {
      console.error('3DS trace: could not emit RUM action', e);
    }
  };

  const trackStep = (step: ThreeDSStep, elapsedMs: number) => {
    sequence.push(step);
    // Last occurrence wins for a step that can repeat (still_pending, page_backgrounded);
    // each occurrence still has its own log line carrying its own elapsed_ms.
    timings[step] = elapsedMs;
  };

  const record = (level: TraceLevel, step: ThreeDSStep, data?: Record<string, any>, cause?: unknown) => {
    const elapsedMs = elapsed();
    lastStep = step;
    trackStep(step, elapsedMs);
    const attributes = {
      ...baseAttributes(),
      step,
      elapsed_ms: elapsedMs,
      ...(data ?? {}),
      ...causeAttributes(cause),
    };
    log(level, attributes, cause);
    rum(step, attributes);
  };

  /**
   * One record carrying the whole sequence, so a stalled or abandoned flow is a single
   * queryable line -- `path` groups every flow of the same shape in one click, `timings` keeps
   * the durations queryable per step (@three_ds.timings.challenge_requested).
   *
   * Deliberately flat. An earlier version sent an array of {step, elapsed_ms} objects, whose
   * field names shadowed the top-level ones: Datadog then resolved a `step` column on this
   * record to @three_ds.breadcrumbs.step, so the terminal record rendered as whichever step
   * the array happened to yield instead of the one it actually recorded.
   */
  const recordTerminal = (level: TraceLevel, step: ThreeDSStep, data?: Record<string, any>, cause?: unknown) => {
    const elapsedMs = elapsed();
    trackStep(step, elapsedMs);
    const attributes = {
      ...baseAttributes(),
      step,
      last_step: lastStep,
      elapsed_ms: elapsedMs,
      path: sequence.join(' > '),
      timings,
      ...(data ?? {}),
      ...causeAttributes(cause),
    };
    log(level, attributes, cause);
    rum(step, attributes);
    lastStep = step;
  };

  const onPageHide = () => {
    // pagehide fires again when a bfcached page is discarded, so report abandonment once.
    if (settled || disposed || abandoned) return;
    abandoned = true;
    // The SDK flushes its batch with sendBeacon on page exit, so this survives the tab going
    // away -- the case that produced no trace at all before.
    recordTerminal('error', ThreeDSStep.ABANDONED, { reason: 'pagehide with flow unsettled' });
  };

  const onVisibilityChange = () => {
    if (settled || disposed || document.visibilityState !== 'hidden') return;
    // Expected mid-challenge on mobile (switching out to read an SMS OTP); it only matters
    // paired with an ABANDONED or a watchdog that never resolves.
    record('info', ThreeDSStep.PAGE_BACKGROUNDED);
  };

  const clearTimers = () => {
    timers.forEach((timer) => clearTimeout(timer));
    timers.length = 0;
  };

  WATCHDOG_MARKS_MS.forEach((mark) => {
    timers.push(
      setTimeout(() => {
        if (settled || disposed) return;
        record('error', ThreeDSStep.STILL_PENDING, { waited_ms: mark, stuck_at: lastStep });
      }, mark),
    );
  });

  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);

  try {
    // Tags the whole RUM session, so a support ticket carrying only a checkout id lands on
    // the session timeline for that payment.
    datadogRum.setGlobalContextProperty(NAMESPACE, { trace_id: traceId, checkout_id: context.checkoutId ?? null });
  } catch (e) {
    console.error('3DS trace: could not set RUM global context', e);
  }

  record('info', ThreeDSStep.EXECUTE_START);

  return {
    traceId,
    step: (step, data) => record('info', step, data),
    fail: (step, data, cause) => {
      markOutcome(ThreeDSOutcome.FAILED);
      record('error', step, data, cause);
    },
    decline: (step, data, cause) => {
      markOutcome(ThreeDSOutcome.DECLINED);
      record('warn', step, data, cause);
    },
    settle: (step, data, cause) => {
      if (settled) return;
      settled = true;
      clearTimers();
      recordTerminal(terminalLevel(step), step, data, cause);
    },
    enrich: (next) => {
      context = { ...context, ...next };
    },
    getContext: () => ({ ...context }),
    dispose: () => {
      if (disposed) return;
      disposed = true;
      clearTimers();
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    },
  };
};
